<template>
  <div class="textarea_wrap">
    <textarea
      ref="baseTextarea"
      :rows="rows"
      class="form_style"
      :class="{ input_error: error }"
      :placeholder="placeholder"
      v-model="model"
      :disabled="disabled"
      :maxlength="maxLengthValue || undefined"
      v-bind="$attrs"
      @update:modelValue="onUpdate"
      @input="onInput"
      @change="onChange"
    />
    <!-- 글자수 표기-->
    <p
      v-if="maxLengthValue"
      class="f13 input_maxlength_counter"
      :class="{
        font_color: currentLength > 0,
        font_red: isOverMaxLength,
      }"
    >
      <span>{{ currentLength }}</span> / {{ maxLengthValue }}
    </p>
  </div>
  <span v-if="error" class="input_error_txt">{{ errorText }}</span>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

defineOptions({
  inheritAttrs: false,
});

/* ------------------------------------------------------------
                          Model
   ------------------------------------------------------------ */
const model = defineModel<string | number | null>({ required: true, default: null });

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | null): void;
  (e: "input", value: string | number | null): void;
  (e: "change", value: string | number | null): void;
}>();

/* ------------------------------------------------------------
                          Props
   ------------------------------------------------------------ */
const props = defineProps({
  placeholder: {
    type: String,
    default: "", // placeholder 값
  },
  error: Boolean, // 에러 여부
  errorText: {
    type: String,
    default: "", // 에러 메시지
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  rows: {
    type: Number,
    default: 4, // 기본적으로 4행
  },
  maxLength: {
    type: [Number, String],
    default: undefined, // 글자수 제한 (camelCase 권장)
  },
  maxlength: {
    type: [Number, String],
    default: undefined, // 글자수 제한 (HTML 속성 호환용, kebab-case로도 전달 가능)
  },
});

/* ------------------------------------------------------------
                          Variable Declarations
   ------------------------------------------------------------ */
const baseTextarea = ref<HTMLInputElement>();

/* ------------------------------------------------------------
                          Computed
   ------------------------------------------------------------ */
const currentLength = computed(() => {
  if (model.value === null || model.value === undefined) return 0;
  return String(model.value).length;
});

const maxLengthValue = computed(() => {
  // maxLength (camelCase) 또는 maxlength (HTML 속성 호환) 중 하나 사용
  return props.maxLength || props.maxlength ? Number(props.maxLength || props.maxlength) : undefined;
});

const isOverMaxLength = computed(() => {
  if (!maxLengthValue.value) return false;
  return currentLength.value >= maxLengthValue.value;
});

const inputClasses = computed(() => {
  return {
    input_error: props.error || isOverMaxLength.value,
    border_none: props.border_none,
  };
});

/* ------------------------------------------------------------
                          Methods
   ------------------------------------------------------------ */
const onUpdate = (e: Event) => {
  const value = (e.target as HTMLInputElement).value;
  // maxLength가 있고 초과하면 제한
  if (maxLengthValue.value && value.length > maxLengthValue.value) {
    (e.target as HTMLInputElement).value = value.substring(0, maxLengthValue.value);
    emit("update:modelValue", value.substring(0, maxLengthValue.value));
    return;
  }
  emit("update:modelValue", value);
};

const onInput = (e: Event) => {
  const value = (e.target as HTMLInputElement).value;
  // maxLength가 있고 초과하면 제한
  if (maxLengthValue.value && value.length > maxLengthValue.value) {
    (e.target as HTMLInputElement).value = value.substring(0, maxLengthValue.value);
    emit("update:modelValue", value.substring(0, maxLengthValue.value));
    emit("input", value.substring(0, maxLengthValue.value));
    return;
  }
  emit("update:modelValue", value);
  emit("input", value);
};

const onChange = (e: Event) => {
  const value = (e.target as HTMLInputElement).value;
  emit("change", value);
};

const focus = () => {
  baseTextarea.value?.focus();
};

/* ------------------------------------------------------------
                          Expose
   ------------------------------------------------------------ */
defineExpose({
  focus,
});
</script>
